'use client'

import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@plura/shared/utils';
import { useEffect, useState } from 'react';

const didYouKnowFacts = [
  "Did you know that you can build intelligent chatbots with the AI Conversation block type.",
  "Did you know you can use Formsuite forms as engaging 404 pages.",
  "Did you know you can use Formsuite as AI support popups on your website.",
  "Did you know you can create dynamic journeys based on how your users interact with your forms.",
  "Did you know you can add segments to your forms.",
  "Did you know you that your CRM uses AI to better understand and summarize your leads."
];

interface Props {
  hideFacts?: boolean,
  animateOut?: boolean,
  dontAnimateIn?: boolean,
  logoUrl?: string | null,
  backgroundImageUrl?: string | null,
  inline?: boolean;
  /** Animation loop duration in seconds. Defaults to 5.5s. */
  loopDuration?: number;
  themeColors?: {
    backgroundColor?: string | null,
    textColor?: string | null,
    buttonColor?: string | null,
    buttonTextColor?: string | null,
  }
}

const GlobalLoadingScreen = ({ hideFacts, animateOut, dontAnimateIn, logoUrl, backgroundImageUrl, themeColors, inline, loopDuration }: Props) => {
  const [animateState, setAnimateState] = useState<'IN' | 'OUT' | 'INIT'>(dontAnimateIn ? 'IN' : 'INIT')
  const [bgImageLoaded, setBgImageLoaded] = useState(false)
  const currentMinute = new Date().getMinutes();
  const factIndex = currentMinute % didYouKnowFacts.length;
  const isBranded = Boolean(logoUrl || themeColors?.buttonColor || themeColors?.backgroundColor || backgroundImageUrl)

  useEffect(() => {
    if (animateState === 'INIT') {
      setAnimateState('IN')
    }
  }, [])

  useEffect(() => {
    if (animateOut) {
      setAnimateState('OUT')
    }
  }, [animateOut])

  const accentColor = themeColors?.buttonColor ?? undefined

  return (
    <AnimatePresence mode="wait" initial={true}>
      <motion.div
        key="loading-screen"
        className={cn(
          'fixed inset-0 z-[9999] flex items-center justify-center p-5 max-md:p-3',
          'transition-[background-color,opacity] duration-500 ease-in-out',
          !isBranded && 'bg-b-surface1',
          animateState === 'OUT' && 'opacity-0 pointer-events-none',
          hideFacts && animateState === 'OUT' && '!bg-transparent',
          inline && '!absolute !inset-0 !h-full !w-full !min-h-0',
        )}
        exit={{ opacity: 0 }}
        initial={{ opacity: 1 }}
        animate={{ opacity: animateState === 'OUT' ? 0 : 1 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        style={
          isBranded
            ? { backgroundColor: themeColors?.backgroundColor ?? accentColor ?? undefined }
            : undefined
        }
      >
        {isBranded && backgroundImageUrl && (
          <img
            src={backgroundImageUrl}
            alt=""
            className={cn(
              'pointer-events-none absolute inset-0 z-0 h-full w-full object-cover object-center opacity-0 transition-opacity duration-500 ease-in-out',
              bgImageLoaded && animateState !== 'OUT' && 'opacity-100',
            )}
            onLoad={() => setBgImageLoaded(true)}
          />
        )}
        <motion.div
          className={cn(
            'relative z-10 flex flex-col items-center justify-center rounded-3xl bg-b-surface2 max-md:rounded-2xl',
            'h-full w-full',
            !themeColors && 'border border-s-stroke2',
            isBranded && backgroundImageUrl && bgImageLoaded && 'shadow-depth-toggle',
          )}
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
          style={{
            ...(themeColors?.backgroundColor ? { backgroundColor: themeColors.backgroundColor } : {}),
          }}
        >
          <div className="flex flex-col items-center gap-4">
            {logoUrl ? (
              <motion.div
                className="flex flex-col items-center justify-center gap-5"
                initial={{ opacity: dontAnimateIn ? 1 : 0, y: dontAnimateIn ? 0 : 20 }}
                animate={{
                  opacity: animateState === 'IN' ? 1 : 0,
                  y: animateState === 'IN' ? 0 : -12,
                }}
                transition={{ duration: 0.35, ease: 'easeOut', delay: dontAnimateIn ? 0.05 : 0.15 }}
              >
                <img
                  src={logoUrl}
                  alt="Logo"
                  className="max-h-[88px] w-full max-w-[280px] object-contain object-center"
                />
                <div className="flex flex-col items-center gap-3">
                  <div
                    className="h-1.5 w-40 overflow-hidden rounded-full"
                    style={{ backgroundColor: accentColor ? `color-mix(in srgb, ${accentColor} 20%, transparent)` : undefined }}
                  >
                    <motion.div
                      className="h-full w-1/3 rounded-full"
                      style={{ backgroundColor: accentColor ?? 'var(--color-t-tertiary)' }}
                      animate={{ x: ['-100%', '300%'] }}
                      transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  </div>
                  {themeColors && (
                    <p
                      className="text-center text-caption"
                      style={{ color: themeColors.textColor ?? undefined }}
                    >
                      powered by <span className="font-semibold">Formsuite</span>
                    </p>
                  )}
                </div>
              </motion.div>
            ) : (
              <div className="relative">
                <AnimatedLogo animateIn={animateState === 'IN'} themeColors={themeColors} loopDuration={loopDuration} />
              </div>
            )}

            {!hideFacts && (
              <motion.p
                className="m-0 max-w-[360px] text-center text-body-2 text-t-tertiary max-md:max-w-60"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: animateState === 'IN' ? 1 : 0, y: animateState === 'IN' ? 0 : 10 }}
                transition={{ duration: 0.3, delay: logoUrl ? 0.45 : 1.6 }}
              >
                &ldquo;{didYouKnowFacts[factIndex]}&rdquo;
              </motion.p>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

/**
 * One loop (repeats): draw border → logo (fill + bars) in → hold →
 * border + logo fade out together → brief invisible stroke reset → repeat.
 */
const LOOP_DURATION = 5.5;

/** Opening trim: border draws on. */
const STROKE_LEG = 0.06;
const T_DRAW_END = STROKE_LEG;

/** Logo block [T_DRAW_END … T_LOGO_OUT_END]: in → hold → out (weights 2:7:3). */
const LOGO_BLOCK_END = 0.92;
const MID = LOGO_BLOCK_END - T_DRAW_END;
const MID_W = 2 + 7 + 3;
const T_LOGO_IN_END = T_DRAW_END + (MID * 2) / MID_W;
const T_HOLD_END = T_LOGO_IN_END + (MID * 7) / MID_W;
const T_LOGO_OUT_END = T_HOLD_END + (MID * 3) / MID_W;

/** Invisible path reset for next loop (opacity 0 while length goes 0). */
const T_STROKE_RESET_END = Math.min(T_LOGO_OUT_END + 0.035, 0.97);

/** Stroke: draw on → stay visible through hold → fade out with logo → reset path invisibly. */
const STROKE_T = [0, T_DRAW_END, T_HOLD_END, T_LOGO_OUT_END, T_STROKE_RESET_END, 1] as const;
const STROKE_PATH_LENGTH = [0, 1, 1, 1, 0, 0] as const;
const STROKE_OPACITY = [1, 1, 1, 0, 0, 0] as const;
const STROKE_EASE = ['easeInOut', 'linear', 'easeOut', 'linear', 'linear'] as const;

/** Fill: hidden during draw; in after outline; hold; out with logo block. */
const FILL_T = [0, T_DRAW_END, T_DRAW_END, T_LOGO_IN_END, T_HOLD_END, T_LOGO_OUT_END, 1] as const;
const FILL_OPACITY = [0, 0, 0, 1, 1, 0, 0] as const;
const FILL_EASE = ['linear', 'linear', 'easeOut', 'linear', 'easeInOut', 'linear'] as const;

const AnimatedLogo = ({ animateIn, themeColors, loopDuration: loopDurationProp }: { animateIn: boolean, themeColors?: Props['themeColors'], loopDuration?: number }) => {
  const dur = loopDurationProp ?? LOOP_DURATION;
  const hexPath = "M152.86,71.84 L127.19,27.37 C123.51,21 114.47,15.78 107.1,15.78 H55.76 c-7.36,0-16.4,5.22-20.08,11.59 L10,71.84 C6.33,78.21 6.33,88.65 10,95 l25.67,44.46 c3.68,6.38 12.72,11.6 20.08,11.6 H107.1 c7.37,0 16.41-5.22 20.09-11.6 L152.86,95 C156.54,88.65 156.54,78.21 152.86,71.84 Z";

  const barTop = "M48.91,55.1 H114 a6.42,6.42,0,1,1,0,12.83 h-65 a6.42,6.42,0,1,1,0-12.83 Z";
  const barMid = "M61.83,79.85 H101 a6.42,6.42,0,0,1,0,12.84 H61.83 a6.42,6.42,0,0,1,0-12.84 Z";
  const barBot = "M71,104.61 H91.86 a6.42,6.42,0,1,1,0,12.83 H71 a6.42,6.42,0,1,1,0-12.83 Z";

  const barTransition = (barTimes: readonly number[]) => ({
    duration: dur,
    repeat: Infinity,
    times: [...barTimes],
    ease: 'easeInOut' as const,
  });

  return (
    <motion.div
      className="relative flex flex-col items-center"
      animate={
        animateIn
          ? {
            filter: [
              'drop-shadow(0 0 0px rgba(42,133,255,0))',
              'drop-shadow(0 0 0px rgba(42,133,255,0))',
              'drop-shadow(0 0 22px rgba(42,133,255,0.2))',
              'drop-shadow(0 0 22px rgba(42,133,255,0.2))',
              'drop-shadow(0 0 0px rgba(42,133,255,0))',
              'drop-shadow(0 0 0px rgba(42,133,255,0))',
            ],
          }
          : {}
      }
      transition={{
        duration: dur,
        repeat: Infinity,
        times: [0, T_DRAW_END, T_LOGO_IN_END, T_HOLD_END, T_LOGO_OUT_END, 1],
        ease: 'easeInOut',
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="-3 -2 168 158"
        className="w-28 h-28 max-md:w-22 max-md:h-22"
      >
        <g transform="translate(0, 0)">
          {/* Hexagon fill — after stroke draw; stays on after border fades */}
          <motion.path
            d={hexPath}
            transform="translate(-7.25, -15.78)"
            fill={themeColors?.buttonColor ?? 'currentColor'}
            className={themeColors?.buttonColor ? undefined : 'text-t-primary'}
            animate={
              animateIn
                ? {
                  opacity: [...FILL_OPACITY],
                }
                : { opacity: 0 }
            }
            transition={{
              duration: dur,
              repeat: Infinity,
              times: [...FILL_T],
              ease: [...FILL_EASE],
            }}
          />

          {/* Hexagon stroke: trim draw on, then opacity off (no trim “undraw”). */}
          <motion.path
            d={hexPath}
            transform="translate(-7.25, -15.78)"
            fill="none"
            stroke={themeColors?.buttonColor ?? 'currentColor'}
            className={themeColors?.buttonColor ? undefined : 'text-t-primary'}
            strokeWidth={4}
            strokeLinecap="butt"
            strokeLinejoin="round"
            pathLength={1}
            initial={{ pathLength: 0, pathOffset: 0, opacity: 1 }}
            animate={
              animateIn
                ? {
                  pathLength: [...STROKE_PATH_LENGTH],
                  pathOffset: 0,
                  opacity: [...STROKE_OPACITY],
                }
                : { pathLength: 0, pathOffset: 0, opacity: 0 }
            }
            transition={{
              duration: dur,
              repeat: Infinity,
              times: [...STROKE_T],
              ease: [...STROKE_EASE],
            }}
          />

          {/* Top bar — same phases as fill; staggered in/out */}
          <motion.path
            d={barTop}
            transform="translate(-7.25, -15.78)"
            style={{ fill: themeColors?.buttonTextColor ?? 'var(--color-b-surface2)' }}
            animate={
              animateIn
                ? {
                  opacity: [0, 0, 0, 1, 1, 1, 0],
                  x: [-18, -18, -18, 0, 0, 0, 18],
                }
                : { opacity: 0, x: 0 }
            }
            transition={barTransition([0, T_DRAW_END, T_DRAW_END + 0.01, T_LOGO_IN_END - 0.02, T_HOLD_END - 0.02, T_LOGO_OUT_END - 0.02, 1])}
          />

          {/* Middle bar */}
          <motion.path
            d={barMid}
            transform="translate(-7.25, -15.78)"
            style={{ fill: themeColors?.buttonTextColor ?? 'var(--color-b-surface2)' }}
            animate={
              animateIn
                ? {
                  opacity: [0, 0, 0, 1, 1, 1, 0],
                  x: [-18, -18, -18, 0, 0, 0, 18],
                }
                : { opacity: 0, x: 0 }
            }
            transition={barTransition([0, T_DRAW_END, T_DRAW_END + 0.02, T_LOGO_IN_END, T_HOLD_END - 0.01, T_LOGO_OUT_END - 0.01, 1])}
          />

          {/* Bottom bar */}
          <motion.path
            d={barBot}
            transform="translate(-7.25, -15.78)"
            style={{ fill: themeColors?.buttonTextColor ?? 'var(--color-b-surface2)' }}
            animate={
              animateIn
                ? {
                  opacity: [0, 0, 0, 1, 1, 1, 0],
                  x: [-18, -18, -18, 0, 0, 0, 18],
                }
                : { opacity: 0, x: 0 }
            }
            transition={barTransition([0, T_DRAW_END, T_DRAW_END + 0.03, T_LOGO_IN_END + 0.02, T_HOLD_END, T_LOGO_OUT_END, 1])}
          />
        </g>
      </svg>
      {themeColors && (
        <motion.p
          className="-mt-1 text-center text-caption"
          style={{ color: themeColors.textColor ?? undefined }}
          animate={
            animateIn
              ? { opacity: [...FILL_OPACITY.map(v => v * 0.5)] }
              : { opacity: 0 }
          }
          transition={{
            duration: dur,
            repeat: Infinity,
            times: [...FILL_T],
            ease: [...FILL_EASE],
          }}
        >
          powered by <span className="font-semibold">Formsuite</span>
        </motion.p>
      )}
    </motion.div>
  );
};

export default GlobalLoadingScreen
